/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * client.ts — client (services/operator-web-app).
 */

/**
 * REST API client for operator-web-app.
 * Uses VITE_API_BASE_URL (or same-origin when empty). All /api/* requests go to web-ui backend.
 */

import { appConfig } from '@/config/env';

function getBaseUrl(): string {
  const base = appConfig.apiBaseUrl.replace(/\/$/, '');
  return base || (typeof window !== 'undefined' ? window.location.origin : '');
}

function apiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  const base = getBaseUrl();
  return `${base}${p}`;
}

export interface ApiError {
  message: string;
  status: number;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json().catch(() => ({}));
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      // ignore
    }
    throw { message, status: res.status } as ApiError;
  }
  const contentType = res.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return res.json() as Promise<T>;
  }
  return undefined as T;
}

/**
 * GET request to API.
 */
export async function apiGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(apiUrl(path));
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') url.searchParams.set(k, v);
    });
  }
  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return handleResponse<T>(res);
}

/**
 * POST request to API with JSON body.
 */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}
